import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, MessageSquare, LogOut, ChevronDown, Sparkles, User } from 'lucide-react';
import { useOnboarding } from '../hooks/useOnboarding';

interface ProfileDropdownProps {
  firstName: string;
  onLogout: () => void;
}

export default function ProfileDropdown({ firstName, onLogout }: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { reset: resetOnboarding, progress } = useOnboarding();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Listen for event to auto-open the dropdown (from DiscoveryTooltip)
  useEffect(() => {
    const handleOpenMenu = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('onboarding:open-user-menu', handleOpenMenu);
    return () => {
      window.removeEventListener('onboarding:open-user-menu', handleOpenMenu);
    };
  }, []);

  const handleStartGuide = async () => {
    // If onboarding is dismissed/skipped, reset it first
    if (progress?.status === 'skipped' || progress?.status === 'completed') {
      try {
        await resetOnboarding();
      } catch (error) {
        console.error('Failed to reset onboarding:', error);
      }
    }
    // Emit event to open widget fully
    window.dispatchEvent(new CustomEvent('onboarding:open-widget'));
    setIsOpen(false);
  };

  const menuItems = [
    {
      icon: Sparkles,
      label: 'Guide de démarrage',
      onClick: handleStartGuide,
      className: 'text-emerald-400',
      dataOnboarding: 'guide-link',
    },
    {
      icon: BookOpen,
      label: 'Documentation',
      onClick: () => {
        navigate('/documentation');
        setIsOpen(false);
      },
    },
    {
      icon: MessageSquare,
      label: 'Nous contacter',
      onClick: () => {
        navigate('/contact');
        setIsOpen(false);
      },
    },
    {
      icon: User,
      label: 'Mon compte',
      onClick: () => {
        navigate('/account');
        setIsOpen(false);
      },
    },
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="group flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold transition-all bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/30"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="h-6 w-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
          <span className="text-xs">{firstName.charAt(0).toUpperCase()}</span>
        </div>
        <span className="text-zinc-300 group-hover:text-white">{firstName}</span>
        <ChevronDown 
          className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-zinc-900 border border-zinc-800 shadow-xl ring-1 ring-black/5 z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
          role="menu"
          aria-orientation="vertical"
        >
          {/* User info header */}
          <div className="px-4 py-3 border-b border-zinc-800">
            <p className="text-sm font-medium text-white">{firstName}</p>
            <p className="text-xs text-zinc-400">Connecté</p>
          </div>

          {/* Menu items */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.onClick}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-300 hover:bg-white/5 hover:text-white transition-colors"
                role="menuitem"
                data-onboarding={item.dataOnboarding}
              >
                <item.icon className={`w-4 h-4 ${item.className || 'text-zinc-400'}`} />
                {item.label}
              </button>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-zinc-800 py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
              role="menuitem"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
